import { Backdrop } from '@loomhq/lens';
import classNames from 'classnames';
import React, { Suspense, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { devToolsEnabled } from 'constants/devtools';
import {
  EXP_INVITE_STORAGE_INCENTIVES_ROLLOUT,
  ROLLOUT_ANONYMOUS_SHARE_PAGE_EDU_BUSINESS,
  ROLLOUT_DESTINATION_FEED_V2,
} from 'constants/featureFlag';
import { STORAGE_INCENTIVES_VARIATIONS_ROLLOUT } from 'constants/incentives';
import { LEGACY_SEARCH_ACCESS } from 'constants/scopes';
import { updateNavigationCollapsed } from 'creators/layout';
import {
  fetchPendingWorkspaceInvites,
  fetchWorkspaces,
} from 'creators/workspace';
import {
  selectAnonCreatorMode,
  selectShareVideoWorkspacePlan,
} from 'selectors/shareVideo';
import { selectUserIsLogged } from 'selectors/user';

import useDestination from 'hooks/useDestination';
import { useFlagIsActivated } from 'hooks/featureFlag';
import { useGetMemberVideoLimits } from 'hooks/workspace';

import AnonymousNavigation from './anonymous-navigation';
import Header from './header';
import HeaderPortalProvider from './header/portal-provider';
import Navigation from './navigation';
import MemberVideoLimitBanner from '../content-limit-banner/MemberVideoLimitBanner';
import WorkspaceContentLimitBanner from '../content-limit-banner/WorkspaceContentLimitBanner';
import DeletionPendingBanner from '../deletion-pending-banner';
import RecorderDownloadBanner from '../recorder-download-banner';
import { HeaderV2 } from './header-v2';
import { useNavigationStore } from './navigation/navigation-store';
import {
  getCustomBrandLogo,
  getLoomBrandVisibility,
} from '../../utilities/custom-branding';
import { WORKSPACE_PLAN_STARTER_FREE } from '../../../../shared/constants/workspacePlans';

import AnonCreatorSignupBanner from 'components/anon-creator-signup-banner';
import { useDevTools } from 'components/devtools/devtools-store';
import ErrorBar from 'components/error-bar';
import DndProvider from 'components/loom-dnd-provider';
import Modal from 'components/modal';
import NavigationBar from 'components/navigation-bar';
import Scopes from 'components/scopes';
import SearchModal from 'components/search-modal';
import ShowSelectWorkspaceModal from 'components/select-workspace-modal';
import GlobalContentLimitBanner from 'components/storage-incentives/global-limit-banner';

import './index.less';

const DevToolsModal = React.lazy(() => import('../devtools/DevToolsModal'));

const Layout = props => {
  const {
    hasCompactBreadcrumb,
    forceAnonNav,
    mainContentHasFullWidth,
    isShareVideo,
  } = props;
  const dispatch = useDispatch();
  const navState = useNavigationStore();

  const showAnonNavForBusiness = useFlagIsActivated({
    flag: ROLLOUT_ANONYMOUS_SHARE_PAGE_EDU_BUSINESS,
    activationValues: [true],
  });

  const shareVideoWorkspacePlan = useSelector(selectShareVideoWorkspacePlan);
  const showDevToolsModal = useDevTools(state => state.showDevToolsModal);
  const navBarIsOpenMobile = navState.isDrawerOpen;

  const navBarState = {
    isCollapsed: navState.isCollapsed,
  };

  const showDestination = useDestination();

  useFlagIsActivated({
    flag: ROLLOUT_DESTINATION_FEED_V2,
    activationValues: [true],
  });

  const hasStorageIncentive = useFlagIsActivated({
    flag: EXP_INVITE_STORAGE_INCENTIVES_ROLLOUT,
    activationValues: STORAGE_INCENTIVES_VARIATIONS_ROLLOUT,
  });

  useEffect(() => {
    dispatch(updateNavigationCollapsed(navBarState.isCollapsed));
  }, [dispatch, navBarState.isCollapsed]);

  const isLoggedIn = useSelector(selectUserIsLogged);
  const anonCreatorMode = useSelector(selectAnonCreatorMode);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchWorkspaces());
      dispatch(fetchPendingWorkspaceInvites());
    }
  }, [dispatch, isLoggedIn]);

  const videoIsOnStarterFreeWorkspace =
    shareVideoWorkspacePlan === WORKSPACE_PLAN_STARTER_FREE;

  const setNavBarIsOpenMobile = useCallback(
    newValue => {
      navState.toggleDrawer(newValue);
    },
    [navState]
  );

  const readyToLoad = isLoggedIn || showAnonNavForBusiness !== undefined;

  const showAnonNav =
    !isLoggedIn &&
    (videoIsOnStarterFreeWorkspace ||
      (showAnonNavForBusiness &&
        getLoomBrandVisibility() &&
        !getCustomBrandLogo()) ||
      forceAnonNav);

  const memberVideoLimits = useGetMemberVideoLimits();

  if (!readyToLoad) {
    return null;
  }

  return (
    <>
      <DndProvider>
        <HeaderPortalProvider>
          {props.useAnonymousNavigation ? (
            <>
              {!showAnonNav && (
                <>
                  <NavigationBar
                    companyFeatureData={props.companyFeatureData}
                  />
                  <main className="mainContentLegacy">
                    <ErrorBar />
                    {props.children}
                  </main>
                </>
              )}
              {showAnonNav && (
                <>
                  <AnonymousNavigation
                    navBarIsOpenMobile={navBarIsOpenMobile}
                    setNavBarIsOpenMobile={setNavBarIsOpenMobile}
                  />
                  <main className="mainContent">
                    {anonCreatorMode && <AnonCreatorSignupBanner />}
                    <ErrorBar />
                    <Header
                      setNavBarIsOpenMobile={setNavBarIsOpenMobile}
                      hasCompactBreadcrumb={hasCompactBreadcrumb}
                      showAnonNav={true}
                    />
                    <div className="mainContentSection hasFullWidth">
                      {props.children}
                    </div>
                  </main>
                </>
              )}
            </>
          ) : (
            <>
              {showDestination !== undefined && (
                <Navigation
                  withDrop={props.withDrop}
                  navBarIsOpenMobile={navBarIsOpenMobile}
                />
              )}
              <main
                className={classNames('mainContent', props.className, {
                  navBarIsCollapsed: navBarState.isCollapsed,
                })}
              >
                <ErrorBar />
                <RecorderDownloadBanner />
                <WorkspaceContentLimitBanner />
                {hasStorageIncentive === false && <MemberVideoLimitBanner />}
                {hasStorageIncentive === true &&
                  memberVideoLimits?.remainingVideos <= 0 && (
                    <GlobalContentLimitBanner
                      memberVideoLimits={memberVideoLimits}
                    />
                  )}
                <DeletionPendingBanner />
                {showDestination && (
                  <>
                    <HeaderV2
                      setNavBarIsOpenMobile={setNavBarIsOpenMobile}
                      isShareVideo={isShareVideo}
                    />
                    <div
                      className={classNames('mainContentSection', {
                        hasFullWidth: mainContentHasFullWidth,
                      })}
                    >
                      {props.children}
                    </div>
                  </>
                )}
                {showDestination === false && (
                  <>
                    <Header
                      setNavBarIsOpenMobile={setNavBarIsOpenMobile}
                      hasCompactBreadcrumb={hasCompactBreadcrumb}
                      showAnonNav={showAnonNav}
                    />
                    <div
                      className={classNames(
                        'mainContentSection',
                        props.className,
                        {
                          hasFullWidth: mainContentHasFullWidth,
                        }
                      )}
                    >
                      {props.children}
                    </div>
                  </>
                )}
              </main>
            </>
          )}
        </HeaderPortalProvider>
      </DndProvider>
      <Scopes names={LEGACY_SEARCH_ACCESS}>
        <Modal>
          <SearchModal />
        </Modal>
      </Scopes>
      <ShowSelectWorkspaceModal />
      <Backdrop
        isOpen={navBarIsOpenMobile}
        zIndex={5}
        onClick={() => setNavBarIsOpenMobile(false)}
      />
      {devToolsEnabled && showDevToolsModal && (
        <Suspense fallback={null}>
          <DevToolsModal />
        </Suspense>
      )}
    </>
  );
};

export default Layout;
